import React, { useRef, useState } from 'react';
import { Paper, Popper } from '@material-ui/core';
import Textarea from '../Textarea-new/Textarea';
import { OTHER_NOTE } from '../../loan-quote-lender-notes';
import './LoanQuoteNotes.scss';

/** A loan quote's Notes box that, while it has focus, offers the notes Peak keeps for the quote's lender. */
const LoanQuoteNotes = ({ id, value, lender, className, tooltip, handleChange, handleInputFocus }) => {
    const [isOpen, setIsOpen] = useState(false);
    const cell = useRef(null);
    const skipNextOpen = useRef(false);
    const isPreset = lender.NOTES.includes(value);

    const open = () => {
        if (skipNextOpen.current) {
            skipNextOpen.current = false;
            return;
        }
        setIsOpen(true);
    };

    const close = ({ refocus = false } = {}) => {
        setIsOpen(false);
        if (refocus && cell.current) {
            skipNextOpen.current = true;
            cell.current.querySelector('textarea').focus();
        }
    };

    // Other clears a picked note to type over; a note already typed by hand stays.
    const choose = (note, { refocus }) => {
        handleChange(id, note === OTHER_NOTE ? (isPreset ? '' : value) : note);
        close({ refocus });
    };

    return (
        <div
            ref={cell}
            onFocus={open}
            onBlur={(e) => !cell.current.contains(e.relatedTarget) && close()}
            onKeyDown={(e) => e.key === 'Escape' && close()}
        >
            <Textarea
                id={id}
                classNameModifier="vertical-table"
                theme="without-border"
                className={className}
                name={id}
                value={value}
                tooltip={tooltip}
                handleParentChange={(key, text) => {
                    close();
                    handleChange(key, text);
                }}
                handleInputFocus={handleInputFocus}
                // Picking a note leaves the focus in the box, so a click has to bring the list back.
                onClick={open}
            />
            <Popper
                open={isOpen}
                anchorEl={cell.current}
                placement="bottom-start"
                className="LoanQuoteNotes_popper"
                // Kept in the cell so Tab walks from the box into the list.
                disablePortal
                modifiers={{ preventOverflow: { boundariesElement: 'viewport' } }}
            >
                <Paper className="LoanQuoteNotes_paper">
                    <ul
                        role="listbox"
                        aria-label={`${lender.NAME} notes`}
                        className="LoanQuoteNotes_list"
                        // Arrow keys would otherwise reach the table, which moves between rows on them.
                        onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === 'Escape') close({ refocus: true });
                        }}
                    >
                        {[...lender.NOTES, OTHER_NOTE].map((note) => (
                            <li
                                key={note}
                                role="option"
                                tabIndex={0}
                                aria-selected={note === OTHER_NOTE ? !!value && !isPreset : note === value}
                                className={note === OTHER_NOTE ? 'LoanQuoteNotes_option LoanQuoteNotes_option--other' : 'LoanQuoteNotes_option'}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => choose(note, { refocus: false })}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        choose(note, { refocus: true });
                                    }
                                }}
                            >
                                {note}
                            </li>
                        ))}
                    </ul>
                </Paper>
            </Popper>
        </div>
    );
};

export default LoanQuoteNotes;
